import React, { useEffect, useState } from 'react';
import { Plus, Edit3, Trash2, X, Save, Video, Phone, MapPin } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const LOCATION_ICON = { online: Video, phone: Phone, in_person: MapPin };

const BLANK = {
  name: '', description: '', duration_minutes: 30, price: 0,
  location_type: 'online', location_detail: '', advisor_ids: [],
  buffer_before: 0, buffer_after: 5, color: '#f5b300', active: true, intake_questions: [],
};

export default function AdminTypesPage() {
  const [types, setTypes] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);

  const load = () => api.adminTypes().then(setTypes);
  useEffect(() => { load(); api.adminAdvisors().then(setAdvisors); }, []);

  const startNew = () => { setForm(BLANK); setEditing('new'); };
  const startEdit = (t) => { setForm({ ...BLANK, ...t }); setEditing(t.id); };

  const save = async () => {
    try {
      const payload = { ...form, price: Number(form.price) || 0, duration_minutes: Number(form.duration_minutes) };
      if (editing === 'new') await api.createType(payload);
      else await api.updateType(editing, payload);
      toast.success('Saved'); setEditing(null); load();
    } catch { toast.error('Save failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Deactivate this appointment type?')) return;
    try { await api.deleteType(id); toast.success('Deactivated'); load(); }
    catch { toast.error('Failed'); }
  };

  const toggleAdvisor = (id) => {
    const has = form.advisor_ids.includes(id);
    setForm({ ...form, advisor_ids: has ? form.advisor_ids.filter((x) => x !== id) : [...form.advisor_ids, id] });
  };

  return (
    <AdminLayout title="Service Types" subtitle="Configurable appointment types"
      actions={<button onClick={startNew} className="inline-flex items-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] px-4 py-2.5 rounded-md text-[13px] font-semibold"><Plus className="w-4 h-4" /> New type</button>}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {types.map((t) => {
          const LocIcon = LOCATION_ICON[t.location_type] || Video;
          return (
            <div key={t.id} className={`rounded-xl bg-white border ${t.active ? 'border-neutral-100' : 'border-rose-200 opacity-70'} p-6`}>
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-lg bg-[#f5b300]/15 flex items-center justify-center">
                  <LocIcon className="w-5 h-5 text-[#c89200]" />
                </div>
                <div className="flex gap-1">
                  <button onClick={() => startEdit(t)} className="w-8 h-8 rounded-md hover:bg-neutral-100 flex items-center justify-center"><Edit3 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(t.id)} className="w-8 h-8 rounded-md hover:bg-rose-50 hover:text-rose-600 flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <h3 className="mt-4 text-[16px] font-bold text-[#14110f]">{t.name}</h3>
              <p className="text-[12.5px] text-neutral-500 line-clamp-2 mt-1">{t.description}</p>
              <div className="mt-4 flex items-center justify-between text-[12px] text-neutral-600">
                <span>{t.duration_minutes} min · {t.location_type}</span>
                <span className="font-bold text-[#c89200]">{t.price > 0 ? `$${t.price}` : 'Free'}</span>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto p-7" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-[22px] font-bold text-[#14110f]">{editing === 'new' ? 'New service type' : 'Edit service type'}</h3>
              <button onClick={() => setEditing(null)} className="w-9 h-9 rounded-full hover:bg-neutral-100 flex items-center justify-center"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" className="w-full px-4 py-3 rounded-md border border-neutral-200 text-[14px] focus:border-[#f5b300] outline-none" />
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Description" className="w-full px-4 py-3 rounded-md border border-neutral-200 text-[14px] focus:border-[#f5b300] outline-none resize-none" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1">Duration (min)</label>
                  <input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} className="w-full px-4 py-3 rounded-md border border-neutral-200 text-[14px] focus:border-[#f5b300] outline-none" />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1">Price ($)</label>
                  <input type="number" value={form.price ?? 0} onChange={(e) => setForm({ ...form, price: e.target.value })} className="w-full px-4 py-3 rounded-md border border-neutral-200 text-[14px] focus:border-[#f5b300] outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1">Location</label>
                  <select value={form.location_type} onChange={(e) => setForm({ ...form, location_type: e.target.value })} className="w-full px-4 py-3 rounded-md border border-neutral-200 text-[14px] focus:border-[#f5b300] outline-none">
                    <option value="online">Online</option>
                    <option value="phone">Phone</option>
                    <option value="in_person">In-person</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-1">Buffer after (min)</label>
                  <input type="number" value={form.buffer_after} onChange={(e) => setForm({ ...form, buffer_after: Number(e.target.value) })} className="w-full px-4 py-3 rounded-md border border-neutral-200 text-[14px] focus:border-[#f5b300] outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-neutral-500 mb-2">Available advisors</label>
                <div className="flex flex-wrap gap-2">
                  {advisors.map((a) => (
                    <button key={a.id} type="button" onClick={() => toggleAdvisor(a.id)} className={`px-3 py-1.5 rounded-full text-[12.5px] font-semibold border transition ${form.advisor_ids.includes(a.id) ? 'bg-[#f5b300] border-[#f5b300] text-[#14110f]' : 'border-neutral-200 hover:border-[#f5b300]'}`}>{a.name}</button>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-[14px]"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#f5b300]" /> Active</label>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => setEditing(null)} className="px-5 py-3 rounded-md border border-neutral-200 hover:bg-neutral-50 text-[14px] font-semibold">Cancel</button>
              <button onClick={save} className="px-5 py-3 rounded-md bg-[#f5b300] hover:bg-[#e0a200] text-[#14110f] text-[14px] font-semibold inline-flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
